<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
// Card "Resumo CRM" do "Histórico de cliente": indicadores, operação CRM,
// janela de cobrança, sincronização, perguntas qualificatórias e ações rápidas.
// Extraído de HistoricoCliente.vue com markup, classes e copy idênticos.
import { computed } from 'vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import {
  ATTENDANCE_WINDOW_REASON_LABEL,
  CRM_SYNC_STATUS_META,
} from '../utils/leadHistoryConstants';
import {
  formatDateTime,
  qualificationQuestionItemsFromLead,
  qualificationQuestionsStatusFromLead,
} from '../utils/leadHistoryPresenter';

const props = defineProps({
  lead: { type: Object, required: true },
  crmStats: { type: Object, required: true },
  crmSummary: { type: Object, default: () => ({}) },
  operationalCounts: { type: Object, default: () => ({}) },
  operationalOwnerLabel: { type: String, default: '' },
  operationalDutyLabel: { type: String, default: '' },
  hasSlaAlerts: { type: Boolean, default: false },
  slaActionWarning: { type: String, default: '' },
  answerDrafts: { type: Object, default: () => ({}) },
  savingAnswerId: { type: [Number, String], default: null },
});

defineEmits([
  'viewTimeline',
  'update:draft',
  'saveAnswer',
  'message',
  'call',
  'newVisit',
]);

const crmSyncSummary = computed(
  () => props.lead.crm_sync || props.lead.additional_attributes?.crm_sync || {}
);
const crmSyncAttempts = computed(() => props.lead.crm_sync_attempts || []);
const latestCrmSyncAttempt = computed(() => crmSyncAttempts.value[0] || null);
const crmSyncStatus = computed(
  () => crmSyncSummary.value.status || 'not_ready'
);
const crmSyncStatusMeta = computed(
  () =>
    CRM_SYNC_STATUS_META[crmSyncStatus.value] || CRM_SYNC_STATUS_META.not_ready
);
const contactability = computed(() => props.lead.contactability || {});
const submittedPhoneNumber = computed(
  () =>
    contactability.value.submitted_phone_number ||
    contactability.value.submittedPhoneNumber ||
    ''
);
const whatsappPhoneNumber = computed(
  () =>
    contactability.value.whatsapp_phone_number ||
    contactability.value.whatsappPhoneNumber ||
    ''
);
const contactPhone = computed(
  () =>
    props.lead.phone_number ||
    props.lead.phoneNumber ||
    whatsappPhoneNumber.value ||
    submittedPhoneNumber.value ||
    ''
);
const hasContactPhone = computed(() => /\d/.test(contactPhone.value));
const latestCrmSyncAttemptLabel = computed(() =>
  latestCrmSyncAttempt.value
    ? `#${latestCrmSyncAttempt.value.attempt_number}`
    : 'Sem tentativa'
);
const latestCrmSyncAttemptJson = computed(() =>
  JSON.stringify(
    {
      payload: latestCrmSyncAttempt.value?.payload,
      response: latestCrmSyncAttempt.value?.response,
    },
    null,
    2
  )
);
const qualificationQuestionsStatus = computed(() =>
  qualificationQuestionsStatusFromLead(props.lead)
);
const qualificationQuestionItems = computed(() =>
  qualificationQuestionItemsFromLead(props.lead)
);
const pendingQualifyingQuestions = computed(
  () => qualificationQuestionsStatus.value.pending_qualifying_questions || []
);
const attendanceWindow = computed(() => props.lead.attendance_window || null);
const attendanceWindowStatus = computed(() => {
  const window = attendanceWindow.value;
  if (!window) return 'Sem janela registrada';
  if (window.meterable === false) return 'Sem cobrança';
  return window.status === 'open' ? 'Janela ativa' : 'Janela encerrada';
});
const attendanceWindowReason = computed(() => {
  const reason = attendanceWindow.value?.invalid_reason;
  return ATTENDANCE_WINDOW_REASON_LABEL[reason] || reason;
});
const questionInputId = question =>
  `lead-qualification-answer-${String(question.id || question.key).replace(
    /[^a-zA-Z0-9_-]/g,
    '-'
  )}`;
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <LaisCard title="Resumo CRM">
    <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <div class="min-w-0 rounded-lais-sm bg-[#f8f4fc] p-3">
        <p class="text-[11px] font-semibold uppercase text-lais-primary">
          Estágio
        </p>
        <p
          class="mt-1 break-words text-[14px] font-semibold text-lais-text [overflow-wrap:anywhere]"
        >
          {{ crmStats.stage }}
        </p>
      </div>
      <div class="min-w-0 rounded-lais-sm bg-[#f5f5f5] p-3">
        <p class="text-[11px] font-semibold uppercase text-lais-text-body">
          Score
        </p>
        <p class="mt-1 text-[14px] font-semibold text-lais-text">
          {{ lead.qualification_score || 0 }}
        </p>
      </div>
      <div class="min-w-0 rounded-lais-sm border border-[#eee5d6] p-3">
        <p class="text-[11px] font-semibold uppercase text-lais-text-muted">
          Visitas
        </p>
        <p
          class="mt-1 break-words text-[14px] font-semibold text-lais-text [overflow-wrap:anywhere]"
        >
          {{ crmStats.visits }} realizadas · {{ crmStats.scheduled }} agendadas
        </p>
      </div>
      <div class="min-w-0 rounded-lais-sm border border-[#eee5d6] p-3">
        <p class="text-[11px] font-semibold uppercase text-lais-text-muted">
          Negócios
        </p>
        <p
          class="mt-1 break-words text-[14px] font-semibold text-lais-text [overflow-wrap:anywhere]"
        >
          {{ crmStats.offers }} propostas · {{ crmStats.favorites }} favoritos
        </p>
      </div>
    </div>

    <div class="mt-4 rounded-lais-sm border border-[#eee5d6] bg-[#fafafa] p-3">
      <div
        class="flex flex-col items-stretch gap-3 sm:flex-row sm:items-start sm:justify-between"
      >
        <div class="min-w-0">
          <p class="text-[11px] font-semibold uppercase text-lais-text-muted">
            Operação CRM
          </p>
          <p
            class="mt-1 break-words text-[14px] font-semibold text-lais-text [overflow-wrap:anywhere]"
          >
            Dono atual: {{ operationalOwnerLabel }}
          </p>
        </div>
        <button
          type="button"
          class="inline-flex min-h-9 w-full items-center justify-center gap-2 rounded-lais border border-[#e5e7eb] px-3 text-[12px] font-semibold text-lais-primary transition-colors hover:border-lais-primary hover:bg-[#f8f4fc] focus-visible:outline-none focus-visible:shadow-lais-focus sm:w-auto"
          aria-label="Ver linha do tempo do CRM"
          @click="$emit('viewTimeline')"
        >
          <span class="i-lucide-history size-4" aria-hidden="true" />
          Ver timeline CRM
        </button>
      </div>

      <dl class="mt-3 grid gap-3 text-[12px] text-lais-text-body">
        <div
          class="grid gap-1.5 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] sm:gap-3"
        >
          <dt class="min-w-0 break-words [overflow-wrap:anywhere]">Plantão</dt>
          <dd
            class="min-w-0 break-words font-semibold text-lais-text sm:text-right [overflow-wrap:anywhere]"
          >
            {{ operationalDutyLabel }}
          </dd>
        </div>
        <div
          class="grid gap-1.5 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] sm:gap-3"
        >
          <dt class="min-w-0 break-words [overflow-wrap:anywhere]">
            Ownership histórico
          </dt>
          <dd
            class="min-w-0 break-words font-semibold text-lais-text sm:text-right [overflow-wrap:anywhere]"
          >
            {{ operationalCounts.ownerships }}
          </dd>
        </div>
        <div
          class="grid gap-1.5 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] sm:gap-3"
        >
          <dt class="min-w-0 break-words [overflow-wrap:anywhere]">
            Tarefas abertas
          </dt>
          <dd
            class="min-w-0 break-words font-semibold text-lais-text sm:text-right [overflow-wrap:anywhere]"
          >
            {{ operationalCounts.tarefasAbertas }} de
            {{ operationalCounts.tarefas }}
          </dd>
        </div>
        <div
          class="grid gap-1.5 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] sm:gap-3"
        >
          <dt class="min-w-0 break-words [overflow-wrap:anywhere]">Repasses</dt>
          <dd
            class="min-w-0 break-words font-semibold text-lais-text sm:text-right [overflow-wrap:anywhere]"
          >
            {{ operationalCounts.repasses }}
          </dd>
        </div>
      </dl>

      <div
        v-if="hasSlaAlerts"
        class="mt-3 rounded-lais-sm border border-rose-100 bg-rose-50 p-3 text-rose-900"
      >
        <p class="text-[13px] font-semibold">SLA em risco antes da ação</p>
        <p
          class="mt-1 break-words text-[12px] leading-relaxed [overflow-wrap:anywhere]"
        >
          {{ slaActionWarning }}
        </p>
      </div>
    </div>

    <div
      v-if="!Object.keys(crmSummary).length"
      class="mt-4 rounded-lais-sm border border-dashed border-[#eee5d6] bg-[#fafafa] p-4 text-center"
    >
      <p class="text-[14px] font-semibold text-lais-text">
        Resumo indisponível
      </p>
      <p class="mt-1 text-[12px] text-lais-text-body">
        Atualize a página para tentar carregar os indicadores do CRM.
      </p>
    </div>

    <div
      v-if="attendanceWindow"
      class="mt-4 rounded-lais-sm border border-[#eee5d6] bg-[#fafafa] p-3"
    >
      <p class="text-[11px] font-semibold uppercase text-lais-text-muted">
        Cobrança de atendimento
      </p>
      <p class="mt-1 text-[14px] font-semibold text-lais-text">
        {{ attendanceWindowStatus }}
      </p>
      <p class="mt-1 text-[12px] leading-relaxed text-lais-text-body">
        Aberta em {{ formatDateTime(attendanceWindow.opened_at) }} · fecha em
        {{ formatDateTime(attendanceWindow.closes_at) }}
      </p>
      <p
        v-if="attendanceWindowReason"
        class="mt-1 text-[12px] font-semibold text-[#c2410c]"
      >
        Motivo: {{ attendanceWindowReason }}
      </p>
    </div>

    <div class="mt-4 rounded-lais-sm border border-[#eee5d6] bg-[#fafafa] p-3">
      <div
        class="flex flex-col items-stretch gap-3 sm:flex-row sm:items-start sm:justify-between"
      >
        <div class="min-w-0">
          <p class="text-[11px] font-semibold uppercase text-lais-text-muted">
            Sincronização CRM
          </p>
          <span
            class="mt-2 inline-flex rounded-full px-2 py-1 text-[11px] font-semibold"
            :class="crmSyncStatusMeta.cls"
          >
            {{ crmSyncStatusMeta.label }}
          </span>
        </div>
        <p
          class="min-w-0 break-words text-left text-[12px] font-semibold text-lais-text-body sm:text-right [overflow-wrap:anywhere]"
        >
          {{ latestCrmSyncAttemptLabel }}
        </p>
      </div>

      <dl class="mt-3 grid gap-3 text-[12px] text-lais-text-body">
        <div
          class="grid gap-1.5 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] sm:gap-3"
        >
          <dt class="min-w-0 break-words [overflow-wrap:anywhere]">
            Última sincronização
          </dt>
          <dd
            class="min-w-0 break-words font-semibold text-lais-text sm:text-right [overflow-wrap:anywhere]"
          >
            {{ formatDateTime(crmSyncSummary.last_synced_at) }}
          </dd>
        </div>
        <div
          v-if="crmSyncSummary.last_error"
          class="grid gap-1.5 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] sm:gap-3"
        >
          <dt class="min-w-0 break-words [overflow-wrap:anywhere]">Erro</dt>
          <dd
            class="min-w-0 break-words font-semibold text-[#b42318] sm:text-right [overflow-wrap:anywhere]"
          >
            {{ crmSyncSummary.last_error }}
          </dd>
        </div>
        <div
          v-if="submittedPhoneNumber"
          class="grid gap-1.5 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] sm:gap-3"
        >
          <dt class="min-w-0 break-words [overflow-wrap:anywhere]">
            Telefone informado
          </dt>
          <dd
            class="min-w-0 break-words font-semibold text-lais-text sm:text-right [overflow-wrap:anywhere]"
          >
            {{ submittedPhoneNumber }}
          </dd>
        </div>
        <div
          v-if="whatsappPhoneNumber"
          class="grid gap-1.5 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] sm:gap-3"
        >
          <dt class="min-w-0 break-words [overflow-wrap:anywhere]">
            WhatsApp efetivo
          </dt>
          <dd
            class="min-w-0 break-words font-semibold text-lais-text sm:text-right [overflow-wrap:anywhere]"
          >
            {{ whatsappPhoneNumber }}
          </dd>
        </div>
      </dl>

      <details v-if="latestCrmSyncAttempt" class="mt-3">
        <summary
          class="cursor-pointer rounded-lais text-[12px] font-semibold text-lais-primary focus-visible:outline-none focus-visible:shadow-lais-focus"
        >
          Payload e resposta
        </summary>
        <textarea
          readonly
          class="mt-2 h-44 w-full resize-none overflow-auto rounded-lais-sm bg-white p-3 text-[11px] text-lais-text"
          :value="latestCrmSyncAttemptJson"
        />
      </details>
    </div>

    <div
      v-if="qualificationQuestionItems.length"
      class="mt-4 rounded-lais-sm border border-[#eee5d6] bg-[#fafafa] p-3"
    >
      <div
        class="flex flex-col items-stretch gap-3 sm:flex-row sm:items-start sm:justify-between"
      >
        <div class="min-w-0">
          <p class="text-[11px] font-semibold uppercase text-lais-text-muted">
            Perguntas qualificatórias
          </p>
          <p
            class="mt-1 break-words text-[12px] text-lais-text-body [overflow-wrap:anywhere]"
          >
            {{
              pendingQualifyingQuestions.length
                ? `${pendingQualifyingQuestions.length} pendente(s)`
                : 'Pronto para envio ao CRM'
            }}
          </p>
        </div>
        <span
          class="inline-flex max-w-full break-words rounded-full px-2 py-1 text-[11px] font-semibold [overflow-wrap:anywhere]"
          :class="
            pendingQualifyingQuestions.length
              ? 'bg-amber-50 text-amber-700'
              : 'bg-emerald-50 text-emerald-700'
          "
        >
          {{ qualificationQuestionsStatus.crm_ready ? 'Completo' : 'Pendente' }}
        </span>
      </div>

      <div class="mt-3 grid gap-3">
        <div
          v-for="question in qualificationQuestionItems"
          :key="question.id || question.key"
          class="grid gap-2 rounded-lais-sm border border-[#e5e7eb] bg-white p-3"
        >
          <div class="flex flex-wrap items-center gap-2">
            <label
              :for="questionInputId(question)"
              class="min-w-0 break-words text-[13px] font-semibold text-lais-text [overflow-wrap:anywhere]"
            >
              {{ question.label }}
            </label>
            <span
              v-if="question.qualifying"
              class="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700"
            >
              Qualificatória
            </span>
            <span
              v-if="question.pending"
              class="rounded-full bg-[#fff7ed] px-2 py-0.5 text-[11px] font-semibold text-[#c2410c]"
            >
              Pendente
            </span>
          </div>
          <div class="flex flex-col gap-2 sm:flex-row">
            <input
              :id="questionInputId(question)"
              :value="answerDrafts[question.id]"
              type="text"
              class="min-w-0 flex-1 rounded-lais-sm border border-[#e5e7eb] bg-white px-3 py-2 text-[13px] text-lais-text focus-visible:outline-none focus-visible:shadow-lais-focus"
              :placeholder="
                question.answer_type === 'money'
                  ? 'Valor informado'
                  : 'Resposta'
              "
              @input="
                $emit('update:draft', {
                  id: question.id,
                  value: $event.target.value,
                })
              "
            />
            <button
              type="button"
              class="inline-flex min-h-9 items-center justify-center gap-2 rounded-lais border border-[#e5e7eb] px-3 text-[12px] font-semibold text-lais-primary transition-colors hover:border-lais-primary hover:bg-[#f8f4fc] focus-visible:outline-none focus-visible:shadow-lais-focus disabled:cursor-not-allowed disabled:opacity-60"
              :aria-busy="savingAnswerId === question.id"
              :aria-label="`Salvar resposta: ${question.label}`"
              :disabled="savingAnswerId === question.id"
              @click="$emit('saveAnswer', question)"
            >
              <span class="i-lucide-save size-4" aria-hidden="true" />
              Salvar
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
      <button
        type="button"
        class="inline-flex min-h-9 w-full items-center justify-center gap-2 rounded-lais border border-[#e5e7eb] px-3 text-[12px] font-semibold text-lais-primary transition-colors hover:border-lais-primary hover:bg-[#f8f4fc] focus-visible:outline-none focus-visible:shadow-lais-focus disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
        aria-label="Enviar mensagem para cliente"
        :disabled="!hasContactPhone"
        :title="
          !hasContactPhone
            ? 'Telefone não informado para este cliente.'
            : undefined
        "
        @click="$emit('message')"
      >
        <span class="i-lucide-message-circle size-4" aria-hidden="true" />
        Enviar mensagem
      </button>
      <button
        type="button"
        class="inline-flex min-h-9 w-full items-center justify-center gap-2 rounded-lais border border-[#e5e7eb] px-3 text-[12px] font-semibold text-lais-primary transition-colors hover:border-lais-primary hover:bg-[#f8f4fc] focus-visible:outline-none focus-visible:shadow-lais-focus disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
        aria-label="Ligar para cliente"
        :disabled="!hasContactPhone"
        :title="
          !hasContactPhone
            ? 'Telefone não informado para este cliente.'
            : undefined
        "
        @click="$emit('call')"
      >
        <span class="i-lucide-phone size-4" aria-hidden="true" />
        Ligar para cliente
      </button>
      <button
        type="button"
        class="inline-flex min-h-9 w-full items-center justify-center gap-2 rounded-lais border border-[#e5e7eb] px-3 text-[12px] font-semibold text-lais-primary transition-colors hover:border-lais-primary hover:bg-[#f8f4fc] focus-visible:outline-none focus-visible:shadow-lais-focus sm:w-auto"
        aria-label="Agendar ou registrar visita"
        @click="$emit('newVisit')"
      >
        <span class="i-lucide-calendar-plus size-4" aria-hidden="true" />
        Agendar visita
      </button>
    </div>
  </LaisCard>
</template>
